// src/pages/WorkerPublicProfile.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Edit3, Upload, Trash2, Save, ArrowLeftCircle } from "lucide-react";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_ORIGIN = (() => {
  try {
    return new URL(API_WRK).origin;
  } catch {
    return "";
  }
})();

const WorkerPublicProfile = () => {
  const [worker, setWorker] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [aboutMe, setAboutMe] = useState("");
  const [experience, setExperience] = useState("");

  // ⭐ NEW — Preview + Selected File
  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const workerId = localStorage.getItem("workerId");

  // Load Profile
  useEffect(() => {
    if (!workerId) return;
    loadProfile();
  }, [workerId]);

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API_WRK}/profile/${workerId}`);
      setWorker(res.data);
      setAboutMe(res.data.aboutMe || "");
      setExperience(res.data.experience || "");
    } catch (err) {
      console.error("Error fetching profile:", err);
    }
  };

  const refreshProfile = async () => {
    try {
      const updated = await axios.get(`${API_WRK}/profile/${workerId}`);
      setWorker(updated.data);
    } catch (err) {
      console.error("Failed to refresh profile:", err);
    }
  };

  // Save About Me + Experience
  const handleStorySubmit = async () => {
    try {
      const formData = new FormData();
      formData.append("workerId", workerId);
      formData.append("aboutMe", aboutMe);
      formData.append("experience", experience);

      await axios.post(`${API_WRK}/update-showcase`, formData);
      setEditMode(false);
      await refreshProfile();
    } catch (err) {
      console.error("Failed to save story:", err);
    }
  };

  // ⭐ NEW — Handle File Select + Instant Preview
  const handleFileSelect = (file) => {
    if (!file) return;
    setUploadedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  // Upload Image
  const handleFileUpload = async () => {
    if (!uploadedFile) return;
    try {
      const formData = new FormData();
      formData.append("workerId", workerId);
      formData.append("portfolio", uploadedFile);

      await axios.post(`${API_WRK}/update-showcase`, formData);

      // reset after upload
      setUploadedFile(null);
      setPreview(null);
      await refreshProfile();
    } catch (err) {
      console.error("Upload error:", err);
    }
  };

  // ⭐ FIXED — Delete Photo (DELETE request)
  const handleDeletePhoto = async (imageUrl) => {
    try {
      await axios.delete(`${API_WRK}/delete-portfolio`, {
        data: { workerId, imageUrl },
      });
      await refreshProfile();
    } catch (err) {
      console.error("Failed to delete photo:", err);
    }
  };

  if (!worker)
    return (
      <div className="min-h-screen flex items-center justify-center text-lg text-gray-600">
        Loading profile...
      </div>
    );

  const profileImg = `${API_ORIGIN}${worker.profilePhotoUrl || "/uploads/default-profile.png"}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 py-8 px-4 md:px-12">

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl overflow-hidden border border-gray-200"
      >
        <div className="relative bg-gradient-to-r from-indigo-600 to-blue-500 h-40 flex items-end justify-center">
          <img
            src={profileImg}
            alt="Profile"
            className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg absolute -bottom-12"
          />
        </div>

        <div className="pt-16 px-6 pb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-800">{worker.name}</h1>
          <p className="text-gray-500 text-sm">
            {worker.city}, {worker.province}
          </p>
          <p className="mt-1 text-sm text-indigo-600 font-medium">
            {worker.tier ? worker.tier.toUpperCase() : "SILVER"} Tier
          </p>

          <div className="mt-4 flex justify-center gap-3">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 text-gray-700 bg-gray-200 px-4 py-2 rounded-full hover:bg-gray-300 transition"
            >
              <ArrowLeftCircle size={16} /> Back
            </button>

            <button
              onClick={() => setEditMode(!editMode)}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-full hover:bg-indigo-700 transition"
            >
              <Edit3 size={16} />
              {editMode ? "Cancel" : "Edit Profile"}
            </button>
          </div>
        </div>
      </motion.div>

      {/* ABOUT ME */}
      <div className="max-w-4xl mx-auto mt-8 space-y-6">
        {/* About Me */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow p-6"
        >
          <h2 className="text-lg font-semibold text-gray-800">🌟 About Me</h2>
          {!editMode ? (
            <p className="mt-2 text-gray-600 text-sm leading-relaxed">
              {worker.aboutMe || "No introduction yet. Add a short story about yourself."}
            </p>
          ) : (
            <textarea
              value={aboutMe}
              onChange={(e) => setAboutMe(e.target.value)}
              className="w-full mt-3 p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-400 text-gray-700"
              rows={4}
            />
          )}
        </motion.div>

        {/* EXPERIENCE */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow p-6"
        >
          <h2 className="text-lg font-semibold text-gray-800">🛠️ Experience & Skills</h2>
          {!editMode ? (
            <p className="mt-2 text-gray-600 text-sm leading-relaxed">
              {worker.experience || "No experience listed yet."}
            </p>
          ) : (
            <textarea
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              className="w-full mt-3 p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-400 text-gray-700"
              rows={4}
            />
          )}
        </motion.div>

        {/* SAVE BUTTON */}
        {editMode && (
          <div className="text-center">
            <button
              onClick={handleStorySubmit}
              className="bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-6 py-3 rounded-full font-semibold shadow hover:shadow-lg flex items-center justify-center gap-2 mx-auto transition"
            >
              <Save size={18} /> Save Changes
            </button>
          </div>
        )}

        {/* PORTFOLIO */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow p-6"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-800">📸 Work Showcase</h2>

            {editMode && (
              <div className="flex gap-3 items-center">

                {/* Upload Button */}
                <label className="flex items-center gap-2 text-sm bg-indigo-600 text-white px-3 py-2 rounded-full cursor-pointer hover:bg-indigo-700 transition">
                  <Upload size={16} /> Upload
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                  />
                </label>

                {/* Add Button */}
                <button
                  onClick={handleFileUpload}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm"
                >
                  Add
                </button>
              </div>
            )}
          </div>

          {/* ⭐ PREVIEW SELECTED IMAGE */}
          {preview && editMode && (
            <div className="relative overflow-hidden rounded-xl shadow mb-4">
              <img
                src={preview}
                className="w-full h-36 object-cover rounded-xl"
                alt="Preview"
              />
              <button
                onClick={() => {
                  setPreview(null);
                  setUploadedFile(null);
                }}
                className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}

          {/* Portfolio List */}
          {worker.portfolio?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {worker.portfolio.map((item, i) => {
                const imgSrc = item.imageUrl.startsWith("http")
                  ? item.imageUrl
                  : `${API_ORIGIN}${item.imageUrl}`;
                return (
                  <div
                    key={i}
                    className="relative overflow-hidden rounded-xl shadow hover:shadow-lg transition"
                  >
                    <img
                      src={imgSrc}
                      alt={`Work ${i + 1}`}
                      className="w-full h-36 object-cover rounded-xl"
                    />
                    {editMode && (
                      <button
                        onClick={() => handleDeletePhoto(item.imageUrl)}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No work showcase yet.</p>
          )}
        </motion.div>

        {/* STATS */}
        <motion.div whileHover={{ scale: 1.01 }} className="grid grid-cols-3 gap-3 mt-8 text-center">
          <div className="bg-white/80 p-4 rounded-xl shadow border border-gray-200">
            <p className="text-green-600 text-xl font-bold">100%</p>
            <p className="text-sm text-gray-600">Satisfaction</p>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-gray-200">
            <p className="text-red-500 text-xl font-bold">0%</p>
            <p className="text-sm text-gray-600">Cancellation</p>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-gray-200">
            <p className="text-blue-600 text-xl font-bold">100%</p>
            <p className="text-sm text-gray-600">Acceptance</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WorkerPublicProfile;
